/**
 * SettingsScreen — "/settings", the consumer's account counter.
 *
 * WHAT THIS SCREEN IS FOR
 *
 * On mobile this is also the "Ещё" tab — for most users it is the ONLY place
 * they will ever change the language or the theme, so those are first-class
 * rows here, not an afterthought parked under an "Appearance" accordion.
 *
 * THE 2026-08 REBUILD
 *
 * Before: six floating white cards stacked down the page, each holding one or
 * two rows, separated by equal gaps. Three concrete problems:
 *   - no hierarchy (six equal cards read as six peers);
 *   - almost no row stated its current value, so "Язык интерфейса" told you
 *     nothing until you looked at which segment happened to be lit;
 *   - "Удалить аккаунт" sat in an ordinary card, one row below "Скачать мои
 *     данные", in ordinary weight — a destructive action a thumb away from a
 *     harmless one.
 *
 * After: ONE ruled slip (see `features/account/components/Slip.tsx`) divided
 * into four bands — who you are / how it looks and speaks / what it tells you /
 * your data. Every row states its current value on the right. Destructive
 * actions live in a separate danger sheet below a deliberate gap, in the
 * semantic danger token, each behind a confirmation: log-out behind a
 * consequence-naming `ConfirmDialog`, deletion behind the stronger typed-phrase
 * `DeleteAccountDialog`.
 *
 * Change-persistence (the direction's raise): flipping a preference marks its
 * row in place with `.g-mark` until the next interaction. The row does not move
 * and the mark does not time out — you always see WHICH thing you just changed,
 * which matters on a screen where four controls look alike.
 *
 * Errors name themselves: every failure path goes through `notify` with a real
 * message and, where the call site can retry, a retry action. No
 * `toast.error(t("error.generic"))`.
 *
 * Behaviour, routes, data flow and every `data-testid` are unchanged
 * (`SettingsScreen.test.tsx`, `account-change-phone.spec.ts`).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Avatar, Button, Segmented, Skeleton, type SegmentedOption } from "@garden/ui";

import { Stagger, StaggerItem } from "@/components/motion";
import { ConfirmDialog } from "@/features/account/components/ConfirmDialog";
import { DeleteAccountDialog } from "@/features/account/components/DeleteAccountDialog";
import {
  DangerZone,
  Slip,
  SlipMessage,
  SlipPage,
  SlipRow,
  SlipSection,
  SlipTitle,
  useSeenMark,
} from "@/features/account/components/Slip";
import { deleteAccount, type MeProfile } from "@/features/account/api";
import { endLocalSession } from "@/features/chat/session-end";
import { ApiError } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { notify } from "@/lib/notify";
import { queryClient } from "@/lib/queryClient";
import { useUiStore, type Theme } from "@/lib/ui-store";
import { UI_LANGUAGES, type UiLanguage } from "@/features/auth/schemas";

import {
  PALETTE_OPTIONS,
  downloadMyData,
  logout,
  registerSettingsI18n,
  useMe,
} from "@/features/settings/api";
import { Toggle } from "@/features/settings/Toggle";

/** Up-to-two-character initials for the profile avatar (decorative). */
function initialsOf(profile: MeProfile | undefined): string {
  const source = (profile?.display_name ?? "").trim() || (profile?.email ?? "").trim();
  if (source === "") return "";
  const parts = source.split(/\s+/).filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`
      : source.slice(0, 2);
  return letters.toUpperCase();
}

/** Resolve a (possibly "system") store theme to the binary Segmented value. */
function themeSegmentValue(theme: Theme): "light" | "dark" {
  return theme === "dark" ? "dark" : "light";
}

export function SettingsScreen(): ReactElement {
  registerSettingsI18n();
  const { t } = useTranslation("settings");
  const navigate = useNavigate();

  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const palette = useUiStore((s) => s.palette);
  const setPalette = useUiStore((s) => s.setPalette);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const { data: profile, isLoading, isError, refetch } = useMe();

  // Notification prefs are local-only at MVP (no backend pref endpoint yet) —
  // the section says so rather than implying they follow the account.
  const [emailOn, setEmailOn] = useState(true);
  const [smsOn, setSmsOn] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // The change-persistence raise: the row you just changed holds its mark.
  const { marked, mark } = useSeenMark();

  const langOptions: SegmentedOption[] = [
    { value: "ru", label: "Русский" },
    { value: "ky", label: "Кыргызча" },
    { value: "en", label: "English" },
  ];
  const themeOptions: SegmentedOption[] = [
    { value: "light", label: t("light") },
    { value: "dark", label: t("dark") },
  ];

  const onSelectLanguage = (value: string): void => {
    if ((UI_LANGUAGES as readonly string[]).includes(value)) {
      setLanguage(value as UiLanguage);
      mark("language");
    }
  };
  const onSelectTheme = (value: string): void => {
    if (value === "light" || value === "dark") {
      setTheme(value);
      mark("theme");
    }
  };

  /**
   * The export is a long-running server job (it zips the profile, the threads
   * and the audit log), which is exactly what `notify.promise` is for: one
   * toast that carries the state instead of a spinner the user has to watch.
   * `downloadMyData` resolves an outcome rather than throwing, so it is
   * re-thrown here to drive the toast's error branch with a real reason.
   */
  const onExport = (): void => {
    if (exporting) return;
    setExporting(true);
    const run = downloadMyData().then((outcome) => {
      if (outcome !== "ok") throw new Error(outcome);
      return outcome;
    });
    notify.promise(run, {
      loading: t("exportPreparing"),
      success: t("exportReady"),
      error: (err) =>
        err instanceof Error && err.message === "rate_limited"
          ? t("exportRateLimited")
          : t("exportError"),
    });
    void run.catch(() => undefined).finally(() => setExporting(false));
  };

  const onConfirmDelete = async (confirmPhrase: string): Promise<boolean> => {
    try {
      await deleteAccount(confirmPhrase);
      clearAuth();
      // W0.3: account deletion ends the session too — the query cache and the
      // chat store must not survive it either. The navigate() below is an SPA
      // transition, not a reload, so without this the previous person's open
      // thread (and cached profile/threads) would still be sitting in this
      // tab for whoever uses this device next.
      queryClient.clear();
      endLocalSession();
      notify.success(t("deleteScheduled"), { description: t("deleteScheduledNote") });
      navigate("/", { replace: true });
      return true;
    } catch (err) {
      // A bad confirm phrase is surfaced by the dialog itself; anything else is
      // a real failure the user should be told about by name.
      if (
        err instanceof ApiError &&
        err.code === "account.delete.confirm_phrase_invalid"
      ) {
        return false;
      }
      notify.error(t("deleteError"), {
        description: t("deleteErrorNote"),
        id: "account-delete",
      });
      return false;
    }
  };

  const onLogout = async (): Promise<void> => {
    setLoggingOut(true);
    try {
      await logout();
      setLogoutOpen(false);
      navigate("/", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const initials = initialsOf(profile);
  const hasPhone = profile?.phone_e164 != null && profile.phone_e164 !== "";
  const paletteName = t(`paletteNames.${palette}`);

  return (
    <SlipPage>
      <Stagger>
        <StaggerItem>
          <SlipTitle title={t("title")} lede={t("lede")} />
        </StaggerItem>

        <StaggerItem>
          <Slip testId="settings-slip">
            {/* ── Who you are ─────────────────────────────────────────── */}
            <SlipSection caption={t("accountSection")} note={t("accountNote")}>
              {isError ? (
                <SlipMessage
                  tone="error"
                  role="alert"
                  testId="settings-profile-error"
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void refetch()}
                      data-testid="settings-profile-retry"
                    >
                      {t("profileRetry")}
                    </Button>
                  }
                >
                  {t("profileLoadError")}
                </SlipMessage>
              ) : isLoading ? (
                <div
                  className="flex items-center gap-3.5 px-4 py-4 md:px-5"
                  data-testid="settings-profile-loading"
                >
                  <Skeleton w={44} h={44} r={999} />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton w="52%" h={14} />
                    <Skeleton w="34%" h={12} />
                  </div>
                </div>
              ) : (
                <SlipRow
                  testId="settings-profile-row"
                  labelTestId="settings-profile-name"
                  valueTestId={hasPhone ? "settings-profile-phone" : undefined}
                  onClick={() => navigate("/account/profile")}
                  ariaLabel={t("profileRowLabel")}
                  leading={
                    <Avatar initials={initials} color="var(--g-primary)" size={44} />
                  }
                  label={profile?.display_name ?? ""}
                  hint={profile?.email ?? ""}
                  value={hasPhone ? profile?.phone_e164 : t("profileNoPhone")}
                  valueAsData={hasPhone}
                />
              )}

              <SlipRow
                testId="settings-sessions"
                to="/account/sessions"
                label={t("sessions")}
                hint={t("sessionsHint")}
              />
            </SlipSection>

            {/* ── How it looks and speaks ─────────────────────────────── */}
            <SlipSection caption={t("appearance")} note={t("appearanceNote")}>
              <SlipRow
                label={t("lang")}
                value={t(`langNames.${language}`)}
                marked={marked === "language"}
              >
                <div data-testid="settings-language">
                  <Segmented
                    full
                    options={langOptions}
                    value={language}
                    onChange={onSelectLanguage}
                  />
                </div>
              </SlipRow>

              <SlipRow
                label={t("theme")}
                value={t(themeSegmentValue(theme))}
                marked={marked === "theme"}
              >
                <div data-testid="settings-theme">
                  <Segmented
                    full
                    options={themeOptions}
                    value={themeSegmentValue(theme)}
                    onChange={onSelectTheme}
                  />
                </div>
              </SlipRow>

              <SlipRow
                label={t("palette")}
                value={paletteName}
                marked={marked === "palette"}
              >
                <div
                  className="flex gap-2"
                  role="radiogroup"
                  aria-label={t("palette")}
                  data-testid="settings-palette"
                >
                  {PALETTE_OPTIONS.map((name) => {
                    const active = palette === name;
                    const label = t("paletteLabel", { name: t(`paletteNames.${name}`) });
                    return (
                      // Each swatch carries `data-palette={name}` so theme.css
                      // resolves THAT palette's --g-primary/--g-accent through
                      // the cascade — token-driven chips, never a literal hex.
                      <button
                        key={name}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-label={label}
                        data-palette={name}
                        data-active={active ? "true" : "false"}
                        data-testid={`settings-palette-${name}`}
                        onClick={() => {
                          setPalette(name);
                          mark("palette");
                        }}
                        className={[
                          "flex h-11 flex-1 items-center justify-center gap-1 rounded-[var(--g-radius)] bg-[var(--g-surface)] p-1",
                          "transition-[border-color,box-shadow] duration-150",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)]",
                          active
                            ? "border border-[var(--g-rule-strong)] shadow-[inset_0_0_0_2px_var(--g-surface),0_0_0_2px_var(--g-ink)]"
                            : "border border-[var(--g-rule)]",
                        ].join(" ")}
                      >
                        <span
                          aria-hidden="true"
                          className="h-[18px] w-[18px] rounded-[5px] bg-[var(--g-primary)]"
                        />
                        <span
                          aria-hidden="true"
                          className="h-[18px] w-[18px] rounded-[5px] bg-[var(--g-accent)]"
                        />
                      </button>
                    );
                  })}
                </div>
              </SlipRow>
            </SlipSection>

            {/* ── What it tells you ───────────────────────────────────── */}
            <SlipSection caption={t("notif")} note={t("notifNote")}>
              <SlipRow
                testId="settings-notif-email"
                label={t("email")}
                hint={t("emailHint")}
                value={emailOn ? t("notifOn") : t("notifOff")}
                marked={marked === "notif-email"}
                trailing={
                  <Toggle
                    on={emailOn}
                    onChange={(next) => {
                      setEmailOn(next);
                      mark("notif-email");
                    }}
                    label={t("email")}
                    testId="settings-toggle-email"
                  />
                }
              />
              <SlipRow
                testId="settings-notif-sms"
                label={t("sms")}
                hint={t("smsHint")}
                value={smsOn ? t("notifOn") : t("notifOff")}
                marked={marked === "notif-sms"}
                trailing={
                  <Toggle
                    on={smsOn}
                    onChange={(next) => {
                      setSmsOn(next);
                      mark("notif-sms");
                    }}
                    label={t("sms")}
                    testId="settings-toggle-sms"
                  />
                }
              />
            </SlipSection>

            {/* ── Your data ───────────────────────────────────────────── */}
            <SlipSection caption={t("dataSection")} note={t("dataNote")}>
              <SlipRow
                testId="settings-export"
                onClick={onExport}
                hideChevron
                disabled={exporting}
                ariaLabel={t("download")}
                label={t("download")}
                hint={t("exportHint")}
                value={exporting ? t("exportPreparing") : "ZIP"}
                valueAsData={!exporting}
              />
              <SlipRow
                testId="settings-privacy-link"
                to="/account/privacy"
                label={t("privacyRow")}
                hint={t("privacyRowHint")}
              />
            </SlipSection>
          </Slip>
        </StaggerItem>

        {/* ── Danger: separated in space, in colour, and behind a confirm ── */}
        <StaggerItem>
          <DangerZone title={t("dangerTitle")} note={t("dangerNote")}>
            <SlipRow
              testId="settings-logout"
              onClick={() => setLogoutOpen(true)}
              hideChevron
              danger
              label={t("logout")}
              hint={t("logoutHint")}
            />
            <SlipRow
              testId="settings-delete"
              onClick={() => setDeleteOpen(true)}
              hideChevron
              danger
              label={t("deleteAcc")}
              hint={t("deleteAccHint")}
            />
          </DangerZone>
        </StaggerItem>

        <StaggerItem>
          <p className="mt-8 text-center text-[12px] text-[var(--g-ink-2)]">
            {t("footer")}
          </p>
        </StaggerItem>
      </Stagger>

      <ConfirmDialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => void onLogout()}
        title={t("logoutConfirmTitle")}
        body={t("logoutConfirmBody")}
        confirmLabel={t("logout")}
        busyLabel={t("logoutBusy")}
        cancelLabel={t("cancel")}
        closeLabel={t("cancel")}
        busy={loggingOut}
        testId="settings-logout-confirm"
      />

      <DeleteAccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />
    </SlipPage>
  );
}

export default SettingsScreen;
